import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift } from "lucide-react";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Empty, Field, Loading, Page, cx } from "../components/wf";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/** Owner only: Family codes that give a company free access. */
export default function FreeCodes() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["billing-status", user?.id], queryFn: () => api("/billing/status") });
  const q = useQuery({ queryKey: ["free-codes"], queryFn: () => api("/billing/codes"), enabled: !!status.data?.is_owner });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (status.isLoading) return <Loading />;
  if (!status.data?.is_owner) return <Navigate to="/" replace />;
  const create = async () => {
    setBusy(true);
    try {
      const c = await api("/billing/codes", { method: "POST", body: { note } });
      setNote("");
      qc.invalidateQueries({ queryKey: ["free-codes"] });
      await navigator.clipboard?.writeText(c.code).catch(() => {});
      toast.success(`Code ${c.code} created and copied`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (code) => {
    try {
      await api(`/billing/codes/${code}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["free-codes"] });
      toast.success("Code turned off");
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <Page title="Free Codes" subtitle="Family codes give free access forever" testid="free-codes-page">
      <div className="mb-6 flex max-w-xl items-end gap-2">
        <Field className="mb-0 flex-1" label="Note (who it's for)" testid="free-code-note" value={note} onChange={setNote} />
        <Btn loading={busy} onClick={create} data-testid="free-code-create">Create Code</Btn>
      </div>
      {q.isLoading ? <Loading /> : !q.data?.length ? <Empty icon={Gift} title="No codes yet" /> : (
        <div className="overflow-hidden rounded-2xl border border-wf-line">
          {q.data.map((c) => (
            <div key={c.code} data-testid={`free-code-${c.code}`} className={cx("flex flex-wrap items-center gap-3 border-b border-wf-line px-5 py-3 last:border-0", c.revoked_at && "opacity-50")}>
              <span className="font-mono font-bold">{c.code}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-wf-mute">{[c.note, c.redeemed_by_email && `redeemed by ${c.redeemed_by_email}`, c.revoked_at && "turned off"].filter(Boolean).join(" · ")}</span>
              {!c.revoked_at && <Btn size="sm" variant="ghost" onClick={() => revoke(c.code)} data-testid={`free-code-revoke-${c.code}`}>Turn off</Btn>}
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
